import { useEffect, useRef } from 'react';
import { motion } from 'framer-motion';

interface AnimatedBackgroundProps {
  variant?: 'particles' | 'gradient' | 'grid' | 'waves' | 'stars';
  intensity?: 'low' | 'medium' | 'high';
  colors?: string[];
}

export default function AnimatedBackground({ 
  variant = 'particles', 
  intensity = 'medium',
  colors = ['#00ff41', '#00ffff', '#ff0080', '#8b00ff']
}: AnimatedBackgroundProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    if (variant === 'particles' && canvasRef.current) {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;

      const particleCount = intensity === 'low' ? 30 : intensity === 'medium' ? 50 : 80;
      const particles: Array<{
        x: number;
        y: number;
        vx: number;
        vy: number;
        size: number;
        color: string;
      }> = [];

      // Initialize particles
      for (let i = 0; i < particleCount; i++) {
        particles.push({
          x: Math.random() * canvas.width,
          y: Math.random() * canvas.height,
          vx: (Math.random() - 0.5) * 0.5,
          vy: (Math.random() - 0.5) * 0.5,
          size: Math.random() * 2 + 1,
          color: colors[Math.floor(Math.random() * colors.length)]
        });
      }

      let animationFrameId: number;

      const animate = () => {
        ctx.clearRect(0, 0, canvas.width, canvas.height);

        particles.forEach((particle, i) => {
          particle.x += particle.vx;
          particle.y += particle.vy;

          if (particle.x < 0 || particle.x > canvas.width) particle.vx *= -1;
          if (particle.y < 0 || particle.y > canvas.height) particle.vy *= -1;

          ctx.beginPath();
          ctx.arc(particle.x, particle.y, particle.size, 0, Math.PI * 2);
          ctx.fillStyle = particle.color;
          ctx.globalAlpha = 0.6;
          ctx.fill();

          // Draw connections
          particles.slice(i + 1).forEach(p2 => {
            const dx = particle.x - p2.x;
            const dy = particle.y - p2.y;
            const distance = Math.sqrt(dx * dx + dy * dy);

            if (distance < 150) {
              ctx.beginPath();
              ctx.moveTo(particle.x, particle.y);
              ctx.lineTo(p2.x, p2.y);
              ctx.strokeStyle = particle.color;
              ctx.globalAlpha = 0.2 * (1 - distance / 150);
              ctx.lineWidth = 1;
              ctx.stroke();
            }
          });
        });

        animationFrameId = requestAnimationFrame(animate);
      };

      animate();

      const handleResize = () => {
        canvas.width = window.innerWidth;
        canvas.height = window.innerHeight;
      };

      window.addEventListener('resize', handleResize);

      return () => {
        cancelAnimationFrame(animationFrameId);
        window.removeEventListener('resize', handleResize);
      };
    }
  }, [variant, intensity, colors]);

  if (variant === 'particles') {
    return (
      <canvas
        ref={canvasRef}
        className="absolute inset-0 pointer-events-none opacity-30"
        style={{ zIndex: 0 }}
      />
    );
  }

  if (variant === 'gradient') {
    return (
      <div className="absolute inset-0 overflow-hidden pointer-events-none" style={{ zIndex: 0 }}>
        <motion.div
          className="absolute inset-0"
          animate={{
            background: [
              `radial-gradient(circle at 20% 50%, ${colors[0]}15 0%, transparent 50%)`,
              `radial-gradient(circle at 80% 50%, ${colors[1]}15 0%, transparent 50%)`,
              `radial-gradient(circle at 50% 20%, ${colors[2]}15 0%, transparent 50%)`,
              `radial-gradient(circle at 20% 50%, ${colors[0]}15 0%, transparent 50%)`,
            ],
          }}
          transition={{
            duration: 10,
            repeat: Infinity,
            ease: 'linear',
          }}
        />
        <motion.div
          className="absolute inset-0"
          animate={{
            background: [
              `radial-gradient(circle at 80% 80%, ${colors[3]}10 0%, transparent 50%)`,
              `radial-gradient(circle at 20% 20%, ${colors[1]}10 0%, transparent 50%)`,
              `radial-gradient(circle at 50% 80%, ${colors[2]}10 0%, transparent 50%)`,
              `radial-gradient(circle at 80% 80%, ${colors[3]}10 0%, transparent 50%)`,
            ],
          }}
          transition={{
            duration: 15,
            repeat: Infinity,
            ease: 'linear',
          }}
        />
      </div>
    );
  }

  if (variant === 'grid') {
    return (
      <div className="absolute inset-0 pointer-events-none opacity-10" style={{ zIndex: 0 }}>
        <div
          className="w-full h-full"
          style={{
            backgroundImage: `
              linear-gradient(rgba(0, 255, 65, 0.1) 1px, transparent 1px),
              linear-gradient(90deg, rgba(0, 255, 65, 0.1) 1px, transparent 1px)
            `,
            backgroundSize: '50px 50px',
          }}
        />
        <motion.div
          className="absolute inset-0"
          animate={{
            backgroundPosition: ['0% 0%', '50px 50px'],
          }}
          transition={{
            duration: 20,
            repeat: Infinity,
            ease: 'linear',
          }}
        />
      </div>
    );
  }

  if (variant === 'waves') {
    return (
      <div className="absolute inset-0 overflow-hidden pointer-events-none" style={{ zIndex: 0 }}>
        {[0, 1, 2].map((i) => (
          <motion.div
            key={i}
            className="absolute inset-0"
            style={{
              background: `linear-gradient(45deg, ${colors[i % colors.length]}08, transparent)`,
              clipPath: `polygon(0 ${100 - i * 20}%, 100% ${80 - i * 20}%, 100% 100%, 0 100%)`,
            }}
            animate={{
              clipPath: [
                `polygon(0 ${100 - i * 20}%, 100% ${80 - i * 20}%, 100% 100%, 0 100%)`,
                `polygon(0 ${80 - i * 20}%, 100% ${100 - i * 20}%, 100% 100%, 0 100%)`,
                `polygon(0 ${100 - i * 20}%, 100% ${80 - i * 20}%, 100% 100%, 0 100%)`,
              ],
            }}
            transition={{
              duration: 8 + i * 2,
              repeat: Infinity,
              ease: 'easeInOut',
            }}
          />
        ))}
      </div>
    );
  }

  if (variant === 'stars') {
    return (
      <div className="absolute inset-0 pointer-events-none" style={{ zIndex: 0 }}>
        {Array.from({ length: intensity === 'low' ? 30 : intensity === 'medium' ? 50 : 100 }).map((_, i) => (
          <motion.div
            key={i}
            className="absolute rounded-full"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              width: Math.random() * 3 + 1,
              height: Math.random() * 3 + 1,
              backgroundColor: colors[Math.floor(Math.random() * colors.length)],
            }}
            animate={{
              opacity: [0.3, 1, 0.3],
              scale: [1, 1.5, 1],
            }}
            transition={{
              duration: 2 + Math.random() * 3,
              repeat: Infinity,
              delay: Math.random() * 2,
              ease: 'easeInOut',
            }}
          />
        ))}
      </div>
    );
  }

  return null;
}

